import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic hook for module-persisted note slot links.
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {object} options.defaultLinks - shape keyed by slot keys
 * @param {() => Promise<object>} options.fetchLinks
 * @param {(partial: object) => Promise<object>} options.patchLinks
 * @param {(err: unknown) => void} [options.onLoadError]
 */
export function useEmbeddedNotepadLinks({
  enabled = true,
  defaultLinks,
  fetchLinks,
  patchLinks,
  onLoadError,
}) {
  const [links, setLinks] = useState(defaultLinks);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const fetchStartedRef = useRef(false);

  const normalize = useCallback(
    (data) => ({
      ...defaultLinks,
      ...(data && typeof data === 'object' ? data : {}),
    }),
    [defaultLinks],
  );

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLinks();
      setLinks(normalize(data));
      setLoadedOnce(true);
    } catch (err) {
      onLoadError?.(err);
      setLoadedOnce(true);
    } finally {
      setLoading(false);
    }
  }, [fetchLinks, normalize, onLoadError]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    loadLinks();
  }, [enabled, loadLinks]);

  const patchLink = useCallback(
    async (slotKey, noteId) => {
      const prev = links[slotKey];
      setLinks((current) => ({ ...current, [slotKey]: noteId }));
      try {
        const data = await patchLinks({ [slotKey]: noteId });
        const normalized = normalize(data);
        setLinks(normalized);
        return normalized;
      } catch (err) {
        setLinks((current) => ({ ...current, [slotKey]: prev }));
        throw err;
      }
    },
    [links, patchLinks, normalize],
  );

  const clearLink = useCallback(
    (slotKey) => patchLink(slotKey, null),
    [patchLink],
  );

  return {
    links,
    setLinks,
    loading,
    loadedOnce,
    patchLink,
    clearLink,
    normalize,
  };
}
